import React from "react";
import * as d3 from "d3";
import "./tooltips.css";

const GraphTooltip = ({
  visible,
  x,
  y,
  data,
  mode,
  aggregateType,
  isRate,
  isMobile, // New prop to detect mobile view
}) => {
  // If not visible or no data, render nothing
  if (!visible || !data) return null;

  const isSummarise = aggregateType === "Summarise";

  // Determine the CSS class for sizing and positioning
  const getDynamicClass = () => {
    // 1. Mobile overrides all width logic -> returns the Pinned Class
    if (isMobile) return "mobile-pinned";

    // 2. Desktop logic
    if (mode === "processing") return "tooltip-width-processing";
    if (isSummarise) return "tooltip-width-summarise";
    return "tooltip-width-default";
  };

  const formatDate = (date) => {
    if (date instanceof Date) {
      return d3.timeFormat("%B %d, %Y")(date);
    }
    return date;
  };

  const formatValue = (val) => {
    return typeof val === "number" ? val.toFixed(2) : "-";
  };

  // --- CONTENT RENDERERS ---

  // 1. Summarise View
  const renderSummariseContent = () => {
    const displayValue = isRate
      ? `${(data.value * 100).toFixed(0)}%`
      : formatValue(data.value);

    return (
      <div
        className="tooltip-row"
        style={{ alignItems: "center", padding: "8px 0" }}
      >
        <span style={{ fontSize: "1.6em", fontWeight: 700 }}>
          {displayValue}
        </span>
      </div>
    );
  };

  // 2. Planning View
  const renderPlanningContent = () => {
    const moments = [1, 2, 3, 4, 5];
    return (
      <div style={{ marginTop: "8px" }}>
        {moments.map((num) => {
          const label = data[`m${num}`];
          const rawScore = data[`m${num}Score`];
          if (!label) return null;

          const score = parseFloat(rawScore) || 0;
          const isDone = score === 10;
          const percentage = Math.round((score / 10) * 100);

          return (
            <div key={`moment-${num}`} className="tooltip-row-flex">
              <span className="tooltip-label label-truncate">{label}</span>
              <span className="tooltip-value value-right">
                {isDone ? "✓" : `${percentage}%`}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  // 3. Processing View
  const renderProcessingContent = () => {
    return (
      <div className="tooltip-stack">
        {data.tooltipA && (
          <div className="tooltip-row">
            <div className="tooltip-label label-upper">Remember Today</div>
            <div className="tooltip-value value-italic">"{data.tooltipA}"</div>
          </div>
        )}
        {data.tooltipB && (
          <div className="tooltip-row">
            <div className="tooltip-label label-upper">Leave Behind</div>
            <div className="tooltip-value value-italic">"{data.tooltipB}"</div>
          </div>
        )}
      </div>
    );
  };

  // 4. Moment Mix View (combination chart)
  const renderMomentMixContent = () => {
    const avgPct = Math.round((data.avgScorePct || 0) * 100);
    const planPct = Math.round((data.planFreqPct || 0) * 100);
    return (
      <div style={{ marginTop: "4px" }}>
        <div className="tooltip-row-flex">
          <span className="tooltip-label">Avg score:</span>
          <span className="tooltip-value">{avgPct}%</span>
        </div>
        <div className="tooltip-row-flex">
          <span className="tooltip-label">Planned:</span>
          <span className="tooltip-value">{planPct}%</span>
        </div>
        <div className="tooltip-row-flex">
          <span className="tooltip-label">Days:</span>
          <span className="tooltip-value">
            {data.loggedCount} of {data.totalDays}
          </span>
        </div>
      </div>
    );
  };

  // 5. Standard View
  const renderStandardContent = () => {
    if (data.amountDone !== undefined && data.feelingScore !== undefined) {
      return (
        <div style={{ marginTop: "4px" }}>
          <div className="tooltip-row-flex">
            <span className="tooltip-label">Done:</span>
            <span className="tooltip-value">
              {formatValue(data.amountDone)}
            </span>
          </div>
          <div className="tooltip-row-flex">
            <span className="tooltip-label">Felt:</span>
            <span className="tooltip-value">
              {formatValue(data.feelingScore)}
            </span>
          </div>
        </div>
      );
    }
    return (
      <div className="tooltip-row-standard">
        <span style={{ fontSize: "1.2em", fontWeight: 600 }}>
          {formatValue(data.value)}
        </span>
        <span className="tooltip-meta">Score</span>
      </div>
    );
  };

  // Calculate position styles
  // If Mobile: Empty object (CSS handles fixed position)
  // If Desktop: Inline top/left based on mouse coordinates
  const positionStyles = isMobile
    ? {}
    : {
        top: y - 10,
        left: x + 20,
      };

  return (
    <div
      className={`tooltip-container tooltip-visible ${getDynamicClass()}`}
      style={positionStyles}
    >
      <div
        className="tooltip-header"
        style={{ textAlign: isSummarise ? "center" : "left" }}
      >
        {formatDate(data.category)}
      </div>

      {isSummarise
        ? renderSummariseContent()
        : mode === "momentMix"
          ? renderMomentMixContent()
          : mode === "planning"
            ? renderPlanningContent()
            : mode === "processing"
              ? renderProcessingContent()
              : renderStandardContent()}
    </div>
  );
};

export default GraphTooltip;
